import { useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useEligibilitySubmission,
  MISSING_DOB_ERROR,
  MISSING_GENDER_ERROR,
  type EligibilityPatient,
} from '@/hooks/useEligibilitySubmission';

export type EligibilitySubmissionFlowResult = {
  /** True when the caller should surface a retry prompt (no navigation happened). */
  retry: boolean;
};

/**
 * RND-35692: shared "submit eligibility → route → map errors" flow.
 *
 * Extracted so the identical routing/error handling runs from both
 * (a) the Behavioral Health condition step when no screener is needed, and
 * (b) the GAD-7 screener step once the PHQ-9/GAD-7 screeners complete — keeping
 * one source of truth for the CMS submission outcome routing (RND-33880).
 *
 * - Success: navigate to the submission's route, return `{ retry: false }`.
 * - Unrecoverable error (missing DOB/gender): route to a terminal screen,
 *   return `{ retry: false }`.
 * - Transient failure / all submissions failed: return `{ retry: true }` so the
 *   caller shows a retry prompt without navigating.
 */
export const useEligibilitySubmissionFlow = () => {
  const navigate = useNavigate();
  const { submit, isSubmitting } = useEligibilitySubmission();
  // The underlying hook's `isSubmitting` flips asynchronously (after this
  // function's own sync guards resolve), so two taps inside that gap would
  // both read isSubmitting===false and both fire a CMS submission. This ref
  // flips synchronously on the very first call, closing that gap.
  const inFlightRef = useRef(false);

  const submitAndRoute = useCallback(async (patient?: EligibilityPatient): Promise<EligibilitySubmissionFlowResult> => {
    if (inFlightRef.current) {
      return { retry: false };
    }
    inFlightRef.current = true;
    try {
      const { route } = await submit(patient);
      if (!route) {
        // Every submission failed — keep the user in place so they can retry.
        return { retry: true };
      }
      navigate(route);
      return { retry: false };
    } catch (err) {
      // A missing/invalid DOB can never be fixed from the screening flow, so a
      // retry would loop forever — route to the not-eligible end state instead.
      if (err instanceof Error && err.message === MISSING_DOB_ERROR) {
        navigate('/not-eligible');
        return { retry: false };
      }
      // A missing gender means the Medicare form has to be re-entered.
      if (err instanceof Error && err.message === MISSING_GENDER_ERROR) {
        navigate('/medicare-coverage');
        return { retry: false };
      }
      // Any other failure is transient — surface a retry.
      return { retry: true };
    } finally {
      inFlightRef.current = false;
    }
  }, [submit, navigate]);

  return { submitAndRoute, isSubmitting };
};
